import React from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTA from "@/components/CTA";
import Seo from "@/components/Seo";
import { ShieldCheck, Sun, Truck, MapPin } from "@phosphor-icons/react";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About 360 View LLC" description="360 View LLC is an NDOT SBE certified provider of solar-powered mobile and modular surveillance systems, serving Nevada and Utah." path="/about" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "About" }]} /></section>
      <section className="container-x pb-16">
        <div className="eyebrow text-solar mb-3">About 360 View</div>
        <h1 className="mb-6 max-w-4xl">Solar surveillance for sites that keep moving.</h1>
        <p className="text-white/80 text-lg max-w-3xl leading-relaxed">
          360 View LLC delivers rapid-deployment, solar-powered, cellular-connected surveillance systems across Nevada and Utah.
          We support construction, commercial, government, utility, event and remote sites — the places where wired infrastructure
          isn't practical and guard-only coverage isn't consistent.
        </p>
      </section>

      <section className="bg-steel border-y border-chrome py-16 md:py-20">
        <div className="container-x grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-chrome border border-chrome">
          {[
            { icon: ShieldCheck, t: "Certified", d: "NDOT Small Business Enterprise Certified. Nevada CCTV approved provider." },
            { icon: Sun, t: "Solar-first", d: "Our systems are built for sites without permanent power." },
            { icon: Truck, t: "Rapid deployment", d: "Typical 24–72 hour turnaround, subject to availability." },
            { icon: MapPin, t: "Regional focus", d: "Nevada active. Utah expanding — contact us to confirm coverage." },
          ].map((c) => (
            <div key={c.t} className="bg-obsidian p-8">
              <c.icon size={32} weight="duotone" className="text-solar mb-4" />
              <div className="font-display uppercase text-xl font-bold mb-2">{c.t}</div>
              <div className="text-white/70 text-sm leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x py-16 md:py-20 grid lg:grid-cols-[1.2fr_1fr] gap-12">
        <div>
          <div className="eyebrow text-solar mb-3">Positioning</div>
          <h2 className="mb-6">Rapid-deployment solar surveillance for sites that cannot afford to go unprotected.</h2>
          <p className="text-white/75 text-lg leading-relaxed mb-4">
            Our Mini, Standard and Pro configurations cover everything from single access points to multi-unit
            perimeter deployments. Every quote begins with a site evaluation — coverage depends on layout, placement and configuration.
          </p>
          <p className="text-white/75 leading-relaxed">
            360 View combines commercial-grade equipment, solar and cellular technology, and a deployment model designed to move
            with your project. We prefer accurate language over hype.
          </p>
        </div>
        <div className="border border-chrome bg-steel p-8">
          <div className="eyebrow text-solar mb-4">What We Won't Say</div>
          <ul className="space-y-3 text-sm text-white/80 leading-relaxed">
            <li>— "Crime-proof" or "guaranteed to prevent theft."</li>
            <li>— That surveillance instantly triggers police response.</li>
            <li>— That every site achieves full coverage without evaluation.</li>
            <li>— That systems operate indefinitely without sunlight.</li>
            <li>— Any certification we can't document.</li>
          </ul>
          <div className="mt-6 pt-6 border-t border-chrome text-xs text-white/50">
            Accurate language protects contractors, government buyers and property owners from unpleasant surprises.
          </div>
        </div>
      </section>

      <CTA title="Ready to protect a site?" note="Every site is unique. Start with a free site assessment." />
    </div>
  );
}
